import { useDataPresentationParam, useDatasourceId, useQueryId } from '@/hooks';
import { useGetSummarizeSuggestionsQuery } from '@/features/suggestions/apiSlice';
import React from 'react';
import { SuggestedSummarizeStep } from '@/features/suggestions/components/subcomponents/SuggestedSummarizeStep';
import { Header } from '@/features/suggestions/components/styled-components';
import { useSelector } from 'react-redux';
import { getQueryBuilderSteps } from '@/store-selectors';
import { AGGREGATE, JOIN } from '@/util/enums/EnumLayers';
import { TrevorIcon } from '@trevor/ui-components';
import AjaxSpinner from '@/components/AjaxSpinner';
import { getStepsSuggestions } from '@/features/suggestions/store-selectors';
import Layer from '@/components/layers/Layer';
import InlineAggregateLayer from '@/components/layers/InlineAggregateLayer';
import { foldPredicates } from '@/util/fp';

const isSummarizeSuggestion: Predicate<Layer> = (l) =>
  l.layerType() === AGGREGATE && l.suggestion;
const isSummarizeTemplate: Predicate<Layer> = (l) =>
  l.layerType() === AGGREGATE && l.isTemplate();
const isSummarizeStep: Predicate<Layer> = (layer) =>
  layer.layerType() === AGGREGATE && !layer.suggestion;
const isJoinStep: Predicate<Layer> = (layer) => layer.layerType() === JOIN;
const isStepEnabled: Predicate<Layer> = (layer) =>
  !layer.isTemplate() && layer.enabled();

const isSuggestionApplied = (layer: InlineAggregateLayer, layers: Layer[]) => {
  return (
    layers.filter(isSummarizeSuggestion).findIndex((l) => {
      const keys = (l as InlineAggregateLayer).keys();
      return (
        keys.length !== 0 &&
        JSON.stringify(keys) === JSON.stringify(layer.keys())
      );
    }) !== -1
  );
};

export const SuggestedLayers: React.FC = () => {
  const datasourceId = useDatasourceId();
  const queryId = useQueryId();
  const dataPresentation = useDataPresentationParam();

  const layers = useSelector(getQueryBuilderSteps);
  const { suggestions: suggestedSteps } = useSelector(getStepsSuggestions);
  const appliedSuggestionIdx = layers.findIndex(isSummarizeSuggestion);
  const appliedSuggestion = layers[appliedSuggestionIdx]?.id() || -1;

  const templateAggregateLayer = layers.findIndex(
    foldPredicates.withOr(isSummarizeSuggestion, isSummarizeTemplate),
  );

  const layerNum =
    (templateAggregateLayer === -1 ? layers.length : templateAggregateLayer) +
    1;

  const hasNotAllowedLayers =
    layers
      .filter(isStepEnabled)
      .findIndex(foldPredicates.withOr(isJoinStep, isSummarizeStep)) !== -1 ||
    (appliedSuggestionIdx !== -1 &&
      layers.slice(appliedSuggestionIdx + 1).findIndex(isStepEnabled) !== -1);

  const { isFetching } = useGetSummarizeSuggestionsQuery(
    { datasourceId, queryId },
    {
      skip:
        !datasourceId ||
        !queryId ||
        hasNotAllowedLayers ||
        appliedSuggestion !== -1 ||
        !!dataPresentation,
    },
  );

  const suggestions = suggestedSteps?.filter(
    (layer) =>
      layer.id() && !isSuggestionApplied(layer as InlineAggregateLayer, layers),
  );

  const showSuggestions =
    !!suggestions && suggestions.length > 0 && !hasNotAllowedLayers;

  if (!showSuggestions) {
    return null;
  }
  return (
    <>
      <Header>
        <TrevorIcon name="light-bulb" />
        <span>'Suggested step'</span>
      </Header>

      <div>
        {isFetching || !queryId ? (
          <AjaxSpinner small />
        ) : (
          suggestions.slice(0, 1).map((layer, i) => {
            return (
              <SuggestedSummarizeStep
                key={i}
                layer={layer}
                layerNum={layerNum}
                templateAggregateLayer={templateAggregateLayer}
                layers={layers}
              />
            );
          })
        )}
      </div>
    </>
  );
};
